/**
 * 管理端灵力发放入口。
 *
 * 用户列表与详情共用同一套弹框与幂等行为，避免各处重复维护金额校验与审计理由表单。
 */

import { useEffect, useState } from "react";
import { Alert, Button, Form, Input, InputNumber, Modal, Tooltip, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { api } from "../services/api";
import { useAuth } from "../services/auth";
import { fmtNumber } from "./format";

export interface PowerGrantTarget {
  userId: string;
  userName: string | null;
  relationshipId: string;
  characterName: string;
  power: number;
}

interface PowerGrantFields {
  amount: number;
  reason: string;
}

export interface PowerGrantResult {
  relationshipId: string;
  transactionId: string;
  amount: number;
  balanceAfter: number;
  reused: boolean;
}

export function PowerGrantPlus({
  onClick,
  disabledReason,
}: {
  onClick: () => void;
  disabledReason?: string;
}) {
  const { can } = useAuth();
  if (!can("relationship.adjust")) return null;

  const button = (
    <Button
      type="text"
      size="small"
      icon={<PlusOutlined />}
      disabled={!!disabledReason}
      aria-label="发放灵力"
      style={{ width: 24, height: 24, padding: 0, color: "var(--miao-brand-600)" }}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
    />
  );

  return (
    <Tooltip title={disabledReason ?? "发放灵力"}>
      {disabledReason ? <span>{button}</span> : button}
    </Tooltip>
  );
}

export function PowerGrantModal({
  target,
  onClose,
  onGranted,
}: {
  target: PowerGrantTarget | null;
  onClose: () => void;
  onGranted: (result: PowerGrantResult) => void;
}) {
  const [form] = Form.useForm<PowerGrantFields>();
  const [actionId, setActionId] = useState(() => crypto.randomUUID());

  useEffect(() => {
    if (!target) return;
    setActionId(crypto.randomUUID());
    form.setFieldsValue({ amount: 100, reason: "" });
  }, [form, target]);

  const grant = useMutation({
    mutationFn: (values: PowerGrantFields) => {
      if (!target) throw new Error("没有选择要发放灵力的关系");
      return api.post<PowerGrantResult>(`/api/v1/admin/users/${target.userId}/power-grants`, {
        relationshipId: target.relationshipId,
        amount: values.amount,
        reason: values.reason.trim(),
        actionId,
      });
    },
    onSuccess: (result) => {
      message.success(`已发放 ${result.amount} 点灵力，当前余额 ${result.balanceAfter}`);
      form.resetFields();
      onGranted(result);
      onClose();
    },
    onError: (error: Error) => message.error(error.message),
  });

  return (
    <Modal
      title={`给 ${target?.userName || "该用户"} 发灵力`}
      open={target !== null}
      onCancel={() => {
        if (!grant.isPending) onClose();
      }}
      onOk={() => form.submit()}
      okText="确认发放"
      confirmLoading={grant.isPending}
      cancelButtonProps={{ disabled: grant.isPending }}
      maskClosable={false}
      forceRender
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={`发放到与「${target?.characterName ?? "—"}」的关系钱包`}
        description={`当前余额：${fmtNumber(target?.power)}。到账会写入灵力流水与管理审计。`}
      />
      <Form<PowerGrantFields>
        form={form}
        layout="vertical"
        onFinish={(values) => grant.mutate(values)}
      >
        <Form.Item
          name="amount"
          label="发放数量"
          rules={[{ required: true, message: "请输入发放数量" }]}
        >
          <InputNumber
            min={1}
            max={1_000_000}
            precision={0}
            style={{ width: "100%" }}
            addonAfter="灵力"
          />
        </Form.Item>
        <Form.Item
          name="reason"
          label="发放原因"
          rules={[
            { required: true, message: "写操作必须填写原因，会进入审计日志" },
            { whitespace: true, message: "发放原因不能只有空格" },
            { max: 200, message: "最多 200 个字" },
          ]}
        >
          <Input.TextArea rows={3} placeholder="例如：活动奖励、问题补偿" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
